import React, { useEffect, useMemo, useState } from 'react'
import { type Unit } from '../../../store/slices/unitsSlice'

interface UnitCarouselCardProps {
    unit: Unit
}
const defaultImage = '/assets/media/placeholders/image_unavailable.png'

export default function UnitCarouselCard({ unit }: UnitCarouselCardProps): JSX.Element {
    const images = useMemo(() => {
        const source = unit?.attributes?.Images ?? (unit as any)?.Images ?? []
        return source.filter((image: unknown): image is string => typeof image === 'string' && image.trim().length > 0)
    }, [unit])
    const [imageIndex, setImageIndex] = useState(0)
    const hasMultipleImages = images.length > 1
    const selectedImage = images[imageIndex] ?? defaultImage

    useEffect(() => {
        setImageIndex((current) => Math.min(current, Math.max(images.length - 1, 0)))
    }, [images.length, unit.id])

    const changeImage = (event: React.MouseEvent<HTMLButtonElement>, direction: -1 | 1): void => {
        event.preventDefault()
        event.stopPropagation()
        setImageIndex((current) => (current + direction + images.length) % images.length)
    }

    return <div className="position-relative w-100 overflow-hidden card-rounded border border-gray-500" style={{ height: '200px' }}>
        <img
            src={selectedImage}
            alt={`Unit ${String(unit.id)} photo ${String(imageIndex + 1)}`}
            className="w-100 h-100 d-block"
            style={{ objectFit: 'cover' }}
            loading="lazy"
            decoding="async"
            onError={(event) => {
                event.currentTarget.onerror = null
                event.currentTarget.src = defaultImage
            }}
        />
        {hasMultipleImages && <>
            <button
                type="button"
                aria-label="Show previous unit image"
                className="btn btn-sm btn-light position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle p-0"
                style={{ width: '32px', height: '32px', lineHeight: '1' }}
                onClick={(event) => changeImage(event, -1)}
            >
                ‹
            </button>
            <button
                type="button"
                aria-label="Show next unit image"
                className="btn btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle p-0"
                style={{ width: '32px', height: '32px', lineHeight: '1' }}
                onClick={(event) => changeImage(event, 1)}
            >
                ›
            </button>
            <span className="position-absolute bottom-0 end-0 m-2 px-2 py-1 rounded bg-dark text-white fs-8">
                {String(imageIndex + 1)} / {String(images.length)}
            </span>
        </>}
    </div>
}
